import { toISODate } from '@backrs_ui/common-ui/src/utils/dateTime.util';

import { ActScores, FocusPref, SatScores, TargetTest, WhichTests } from './satActBooster.types';

export type ScoreSection = 'English' | 'Math' | 'Reading' | 'Science' | 'Reading and Writing';

/** SAT/ACT reporting-category taxonomy. PHM sub-leaves link via `parentCode`. */
export type ScoreCategoryDef = Readonly<{
  code: string;
  test: TargetTest;
  section: ScoreSection;
  name: string;
  /** Shorter label for compact inline inputs; falls back to `name`. */
  shortName?: string;
  /** Sequences categories within a section; group order comes from `SUBSCORE_SECTION_ORDER`. */
  displayOrder: number;
  description?: string;
  /** Set on the PHM sub-leaves; groups them under the PHM rollup. */
  parentCode?: string;
}>;

export const SCORE_CATEGORIES: readonly ScoreCategoryDef[] = [
  {
    code: 'act_eng_pow',
    test: 'act',
    section: 'English',
    name: 'Production of Writing',
    displayOrder: 1,
    description: 'Transitions, organization, rhetoric',
  },
  {
    code: 'act_eng_kol',
    test: 'act',
    section: 'English',
    name: 'Knowledge of Language',
    displayOrder: 2,
    description: 'Precision and concision',
  },
  {
    code: 'act_eng_cse',
    test: 'act',
    section: 'English',
    name: 'Conventions of Standard English',
    shortName: 'Conventions of English',
    displayOrder: 3,
    description: 'Grammar, punctuation',
  },
  {
    code: 'act_math_phm',
    test: 'act',
    section: 'Math',
    name: 'Preparing for Higher Math (PHM)',
    shortName: 'Preparing for Higher Math',
    displayOrder: 4,
  },
  {
    code: 'act_math_phm_nq',
    test: 'act',
    section: 'Math',
    name: 'Number & Quantity',
    displayOrder: 5,
    parentCode: 'act_math_phm',
  },
  {
    code: 'act_math_phm_alg',
    test: 'act',
    section: 'Math',
    name: 'Algebra',
    displayOrder: 6,
    parentCode: 'act_math_phm',
  },
  {
    code: 'act_math_phm_fn',
    test: 'act',
    section: 'Math',
    name: 'Functions',
    displayOrder: 7,
    parentCode: 'act_math_phm',
  },
  {
    code: 'act_math_phm_geo',
    test: 'act',
    section: 'Math',
    name: 'Geometry',
    displayOrder: 8,
    parentCode: 'act_math_phm',
  },
  {
    code: 'act_math_phm_sp',
    test: 'act',
    section: 'Math',
    name: 'Statistics & Probability',
    displayOrder: 9,
    parentCode: 'act_math_phm',
  },
  {
    code: 'act_math_ies',
    test: 'act',
    section: 'Math',
    name: 'Integrating Essential Skills',
    displayOrder: 10,
  },
  { code: 'act_math_mod', test: 'act', section: 'Math', name: 'Modeling', displayOrder: 11 },
  {
    code: 'act_read_kid',
    test: 'act',
    section: 'Reading',
    name: 'Key Ideas & Details',
    displayOrder: 12,
  },
  {
    code: 'act_read_acs',
    test: 'act',
    section: 'Reading',
    name: 'Craft & Structure',
    displayOrder: 13,
  },
  {
    code: 'act_read_iki',
    test: 'act',
    section: 'Reading',
    name: 'Integration of Knowledge & Ideas',
    shortName: 'Integration of Knowledge',
    displayOrder: 14,
  },
  {
    code: 'act_sci_iod',
    test: 'act',
    section: 'Science',
    name: 'Interpretation of Data',
    displayOrder: 15,
  },
  {
    code: 'act_sci_sin',
    test: 'act',
    section: 'Science',
    name: 'Scientific Investigation',
    displayOrder: 16,
  },
  {
    code: 'act_sci_emi',
    test: 'act',
    section: 'Science',
    name: 'Evaluation of Models, Inferences & Experimental Results',
    shortName: 'Evaluation of Models',
    displayOrder: 17,
  },
  {
    code: 'sat_rw_info',
    test: 'sat',
    section: 'Reading and Writing',
    name: 'Information and Ideas',
    displayOrder: 1,
  },
  {
    code: 'sat_rw_craft',
    test: 'sat',
    section: 'Reading and Writing',
    name: 'Craft and Structure',
    displayOrder: 2,
  },
  {
    code: 'sat_rw_expr',
    test: 'sat',
    section: 'Reading and Writing',
    name: 'Expression of Ideas',
    displayOrder: 3,
  },
  {
    code: 'sat_rw_conv',
    test: 'sat',
    section: 'Reading and Writing',
    name: 'Standard English Conventions',
    displayOrder: 4,
  },
  { code: 'sat_math_alg', test: 'sat', section: 'Math', name: 'Algebra', displayOrder: 5 },
  { code: 'sat_math_adv', test: 'sat', section: 'Math', name: 'Advanced Math', displayOrder: 6 },
  {
    code: 'sat_math_data',
    test: 'sat',
    section: 'Math',
    name: 'Problem-Solving and Data Analysis',
    shortName: 'Problem-Solving & Data',
    displayOrder: 7,
  },
  {
    code: 'sat_math_geo',
    test: 'sat',
    section: 'Math',
    name: 'Geometry and Trigonometry',
    displayOrder: 8,
  },
];

export const subscoresForTest = (test: TargetTest): readonly ScoreCategoryDef[] =>
  SCORE_CATEGORIES.filter((category) => category.test === test).sort(
    (a, b) => a.displayOrder - b.displayOrder,
  );

/** Grouping order for the subscore inputs; `displayOrder` is contract and can't be renumbered to change it. */
export const SUBSCORE_SECTION_ORDER: Record<TargetTest, readonly ScoreSection[]> = {
  act: ['Math', 'Science', 'English', 'Reading'],
  sat: ['Reading and Writing', 'Math'],
};

const TEST_BY_CATEGORY_CODE: Record<string, TargetTest> = Object.fromEntries(
  SCORE_CATEGORIES.map((category) => [category.code, category.test]),
);

/** The test a reporting-category code belongs to; undefined for a code outside the taxonomy. */
export const testForCategoryCode = (code: string): TargetTest | undefined =>
  TEST_BY_CATEGORY_CODE[code];

/** Which tests' scores a selection admits. Keyed by test so a new one can't default to admitted. */
export const testsInSelection = (
  whichTests: WhichTests | null,
): Readonly<Record<TargetTest, boolean>> => ({
  act: whichTests === 'ACT' || whichTests === 'Both',
  sat: whichTests === 'SAT' || whichTests === 'Both',
});

// Frozen: immer strips `readonly`, and a value freshly assigned into a draft comes back unproxied.
export const EMPTY_ACT_SCORES: ActScores = Object.freeze({
  composite: null,
  english: null,
  math: null,
  reading: null,
  science: null,
  writing: null,
});

export const EMPTY_SAT_SCORES: SatScores = Object.freeze({
  total: null,
  erw: null,
  math: null,
});

/** Section/composite score fields, ordered to mirror the official report. */
export type SectionField = Readonly<{
  key: keyof ActScores | keyof SatScores;
  min: number;
  max: number;
  optional?: boolean;
}>;

export const ACT_SECTION_FIELDS: readonly SectionField[] = [
  { key: 'composite', min: 1, max: 36 },
  { key: 'math', min: 1, max: 36 },
  { key: 'science', min: 1, max: 36, optional: true },
  { key: 'english', min: 1, max: 36 },
  { key: 'reading', min: 1, max: 36 },
  { key: 'writing', min: 2, max: 12, optional: true },
];

export const SAT_SECTION_FIELDS: readonly SectionField[] = [
  { key: 'total', min: 400, max: 1600 },
  { key: 'erw', min: 200, max: 800 },
  { key: 'math', min: 200, max: 800 },
];

/** Section keys that must be present before the score step can advance, per test. */
export const REQUIRED_ACT_SECTIONS: readonly (keyof ActScores)[] = [
  'composite',
  'math',
  'english',
  'reading',
];

export const REQUIRED_SAT_SECTIONS: readonly (keyof SatScores)[] = ['total', 'erw', 'math'];

export const SCORE_MAX: Record<TargetTest, number> = { act: 36, sat: 1600 };

/** Valid target-score range per test; mirrors the API's save-time validation. */
export const TARGET_SCORE_RANGE: Record<TargetTest, { min: number; max: number }> = {
  act: { min: 1, max: 36 },
  sat: { min: 400, max: 1600 },
};

export const DEFAULT_TARGET_BUMP: Record<TargetTest, number> = { act: 3, sat: 80 };

/**
 * Initial target score to pre-fill on the Targets step: a school-derived goal when
 * there is one, else the current score bumped by the default, capped at the test max.
 * The student can adjust from there.
 */
export const defaultTargetScore = (
  test: TargetTest,
  currentScore: number,
  recommendedGoal: number | null,
): number => recommendedGoal ?? Math.min(SCORE_MAX[test], currentScore + DEFAULT_TARGET_BUMP[test]);

// Ordered option values; display labels live in appStrings.
export const WHICH_TESTS_VALUES: readonly WhichTests[] = ['ACT', 'SAT', 'Both', 'none'];

/** A draft's calendar anchor: the day the student picked, or today. */
export const resolveDraftStartDate = (startDate: string | null): string =>
  startDate ?? toISODate(new Date());

// Today is stored as null so the anchor keeps rolling; an open session never pins a day that passes.
export const normalizeDraftStartDate = (startDate: string | null): string | null =>
  startDate === toISODate(new Date()) ? null : startDate;

export { graduationYearOptions, programStartYearOptions } from '@backrs_ui/common-ui';

export const FOCUS_PREF_VALUES: readonly FocusPref[] = ['single', 'double'];

/** DB weekday numbers (0=Sun) in Mon–Sun display order. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const;

export const DEFAULT_STUDY_DAYS: readonly number[] = [1, 2, 3, 4, 5];

export const HOURS_PER_DAY = { min: 0.5, max: 3, step: 0.5, default: 1.5 } as const;

/**
 * Base bounds for the projection card's "what if I added X hrs/week" slider. The upper bound is
 * taken from the projection curve at render (the slider can't offer a value the curve lacks); these
 * cover the lower bound, step, and resting value.
 */
export const PROJECTION_EXTRA_HOURS = { min: 0, step: 1, default: 0 } as const;

/**
 * Insufficient-schedule warning thresholds, mirrored here for the live in-form estimate only. The
 * server's computed schedule flag is authoritative and always wins on the output page; these exist
 * so the schedule step can react instantly to edits before anything is sent.
 *
 * The total is independent of the other two: 5 hrs/week over 4 weeks is 20 hours, so a plan can
 * clear the rate and the runway and still fall short of the 25-hour bar.
 */
export const MIN_HOURS_PER_WEEK = 5;
export const MIN_WEEKS = 4;
/**
 * A rate this thin flags regardless of runway or total — a light touch stretched over many weeks
 * never builds momentum. Must stay below MIN_HOURS_PER_WEEK.
 */
export const CRITICAL_HOURS_PER_WEEK = 2;
/** Total scheduled hours a plan needs before test day. A product call, not a derived number. */
export const MIN_TOTAL_STUDY_HOURS = 25;

export const MAX_SCHOOLS = 5;

/** Idle time before a target-school keystroke reaches the lookup. */
export const SCHOOL_SEARCH_DEBOUNCE_MS = 300;

/** Shortest target-school term that is sent; matches the floor the lookup enforces itself. */
export const SCHOOL_SEARCH_MIN_LENGTH = 4;

/** Longest target-school list offered at once; a broad term can match far more than is useful. */
export const SCHOOL_SEARCH_RESULT_LIMIT = 25;

/**
 * Longest study window, in days, counting both endpoints. Mirrors the save-time bound, and
 * caps the day-by-day coverage estimate — a native date input otherwise accepts years far
 * enough out to hang the step.
 */
export const MAX_PLAN_SPAN_DAYS = 730;

/** US ZIP: 5 digits, optional +4. */
export const ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
